import {
  resolveEditableSkillByName,
  type SkillContentSeed,
  type SkillDraft,
  type SkillSummary,
} from "@/shared/domain/skills";

/**
 * What the Skills page should do with a parked `client_create_skill` update
 * draft.
 *
 * `wait` means the skill list can't answer yet — notably, a still-loading
 * query can't prove a name absent.
 */
/** Whether the skill list can answer a name lookup yet. */
export type SkillListStatus = "loading" | "error" | "ready";

/**
 * Collapse a skills query into the three states resolution cares about.
 *
 * `isError` is distinguished from `ready` deliberately: a failed query leaves
 * an empty list behind, which is indistinguishable from "the name doesn't
 * exist" unless the caller checks.
 */
export function skillListStatus(query: {
  isLoading: boolean;
  isError: boolean;
}): SkillListStatus {
  if (query.isLoading) {
    return "loading";
  }
  if (query.isError) {
    return "error";
  }
  return "ready";
}

export type UpdateHandoffResolution =
  | { kind: "wait" }
  | {
      kind: "open";
      summary: SkillSummary;
      version: number | null;
      seed: SkillContentSeed;
    }
  | { kind: "error"; message: string };

/**
 * Decide how to apply a parked update draft against the loaded skill list.
 *
 * Split out from the effect that runs it so the timing-sensitive part — what
 * happens before the list settles, and how "absent" is distinguished from
 * "couldn't load" — is testable without mounting the page.
 *
 * Editability is judged with the client-side rules
 * ({@link resolveEditableSkillByName}); the server is authoritative at save,
 * so a match allowed here may still be refused there.
 */
export function resolveUpdateHandoff(args: {
  draft: SkillDraft;
  skills: readonly SkillSummary[];
  userId: string;
  listStatus: SkillListStatus;
}): UpdateHandoffResolution {
  const { draft, skills, userId, listStatus } = args;
  const targetName = draft.targetSkillName;
  if (targetName === undefined) {
    // Only update drafts are parked, so this is unreachable in practice —
    // reported rather than silently treated as a create.
    return {
      kind: "error",
      message: "That skill update was missing its target skill name.",
    };
  }
  if (listStatus === "loading") {
    return { kind: "wait" };
  }
  if (listStatus === "error") {
    // The list never arrived, so absence is unproven. Saying "no skill by that
    // name" here would blame the name for a network failure.
    return {
      kind: "error",
      message:
        `Couldn't open “${targetName}” for editing: your skills didn't load. ` +
        `Retry once the list is available.`,
    };
  }
  const match = resolveEditableSkillByName(skills, targetName, userId);
  if (match === null) {
    return {
      kind: "error",
      message:
        `Couldn't open “${targetName}” for editing: there's no skill by that ` +
        `name that you can edit. Create a new skill instead, or check the name.`,
    };
  }
  return {
    kind: "open",
    summary: match,
    // Edit the latest version in place; the human still chooses "Save" vs
    // "Save as new version" in the editor.
    version: match.latest_version?.version ?? null,
    // Tag changes travel with the content even though they belong to the skill
    // rather than the version: the approval card previews them, so the editor
    // is where the user accepts or rejects them (staged, not applied — see
    // `SkillContentSeed`).
    seed: {
      description: draft.description,
      body: draft.body,
      tags: draft.tags,
      removeTags: draft.removeTags,
    },
  };
}
